import { Flex, Text, Badge, IconButton } from "@radix-ui/themes";
import { useCallback, useMemo } from "react";
import { DotsVerticalIcon, TrashIcon } from "@radix-ui/react-icons";
import {
  ExploreInteractionRow,
  ExploreInteractionType,
} from "@/v2/entities/explore/Explore.model";
import { DateFormatter } from "@/v2/components/formatters/DateFormatter";
import { CategoryFormatter } from "@/v2/components/formatters/CategoryFormatter";
import { TopicFormatter } from "@/v2/components/formatters/TopicFormatter";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import styles from "../../styles.module.scss";
import {
  GenericInteractionTypeBadge,
  GenericPendingReviewBadge,
  GenericSubmitterBadge,
  GenericFeedbackBadge,
} from "./shared/SessionMetadataComponents";

interface TrainingPanelInteractionCardProps {
  interaction: ExploreInteractionRow;
  interactionType: ExploreInteractionType | null;
  onClick: () => void;
  isPendingReview?: boolean;
  trainingReason?: string;
  submitter?: string;
  onRemove?: (e: React.MouseEvent) => void;
}

export const TrainingPanelInteractionCard = ({
  interaction,
  interactionType,
  onClick,
  isPendingReview = false,
  trainingReason,
  submitter,
  onRemove,
}: TrainingPanelInteractionCardProps) => {
  const displayId = useMemo(() => {
    return interaction.displayId || interaction.id;
  }, [interaction.displayId, interaction.id]);

  const description = useMemo(() => {
    if (interactionType === "tickets") {
      return interaction.description || interaction.subject;
    }
    return interaction.subject;
  }, [interactionType, interaction.description, interaction.subject]);

  const CategoryBadge = useCallback(() => {
    if (!interaction.category) return null;

    if (
      interaction.category === "POSITIVE" ||
      interaction.category === "NEGATIVE" ||
      interaction.category === "FORGOTTEN"
    ) {
      return <GenericFeedbackBadge category={interaction.category} />;
    }

    return (
      <Text size="1" color="gray" highContrast>
        <CategoryFormatter value={interaction.category} />
      </Text>
    );
  }, [interaction.category]);

  const handleIconButtonClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Flex
      onClick={onClick}
      direction="column"
      className={styles.interactionCard}
      p="2"
    >
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center">
          <Text size="2" weight="medium" color="gray" highContrast>
            #{displayId}
          </Text>
          <Flex gap="2" align="center">
            <GenericInteractionTypeBadge interactionType={interactionType} />
            {isPendingReview && onRemove && (
              <DropdownMenu
                items={[
                  {
                    label: "Remove from review",
                    icon: <TrashIcon width={12} height={12} />,
                    onClick: onRemove,
                  },
                ]}
                align="end"
              >
                <DropdownMenu.Trigger>
                  <IconButton
                    variant="ghost"
                    size="1"
                    color="gray"
                    aria-label="Actions"
                    onClick={handleIconButtonClick}
                  >
                    <DotsVerticalIcon width={16} height={16} />
                  </IconButton>
                </DropdownMenu.Trigger>
              </DropdownMenu>
            )}
          </Flex>
        </Flex>

        <Text size="2" highContrast truncate>
          {description}
        </Text>

        {trainingReason && (
          <Text size="1" color="gray">
            Reason: {trainingReason}
          </Text>
        )}

        <Flex className={styles.interactionMeta}>
          <Flex gap="2" wrap="wrap" align="center">
            {interaction.topic && (
              <Text size="1" color="gray" highContrast>
                <TopicFormatter value={interaction.topic} />
              </Text>
            )}
            <CategoryBadge />
            <GenericPendingReviewBadge isPendingReview={isPendingReview} />
            <GenericSubmitterBadge submitter={submitter} />
          </Flex>

          {interaction.createdAt && (
            <Text size="1" color="gray" style={{ minWidth: "80px" }}>
              <DateFormatter value={interaction.createdAt} />
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
